import { useState, useMemo, useRef, useEffect } from "react";
import {
  Text,
  Table,
  Button,
  Flex,
  Spinner,
  Badge,
  HoverCard,
  Section,
} from "@radix-ui/themes";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { posthogTrackEvent, OpportunitiesEvents } from "../../assets/posthog";
import { useReleases } from "../simulator/context/release";
import { SimulatorChannelIcon } from "../simulators/SimulatorChannelIcon";
import { ReleaseResourceAccessStatus } from "../simulator/types";
import {
  DotsVerticalIcon,
  PlusIcon,
  Pencil1Icon,
  TrashIcon,
  PlayIcon,
  ClockIcon,
} from "@radix-ui/react-icons";
import { SimulatorChannel, Instance } from "@/api/simulator/simulator.model";
import styles from "./TrainingCenter.module.scss";
import { DeleteSimulatorDialog } from "./delete-ai-instance";
import { CreateAIAgentDialog } from "./CreateAIAgentDialog";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { useGetSimulatorReleases } from "@/api/simulator";
import DropdownMenu, {
  DropdownMenuItem,
} from "@/components/shared/dropdown-menu/DropdownMenu";
import { useLiveEnabledTopics } from "@/routes/simulator/useLiveEnabledTopics";
import type { SimulatorReleaseLite } from "@/routes/simulator/types";
import { AiBrowser } from "@/v2/assets/icons/AiBrowser";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";

type SortField = "name" | "type" | "topics" | "lastPublished" | null;
type SortDirection = "asc" | "desc";

interface TypeInfo {
  label: string;
  className: string;
}

interface AiInstanceProps {
  instance: Instance;
  typeInfo: TypeInfo;
  renderTopicBadges: (topics: string[]) => React.ReactNode;
  formatDate: (dateString: string) => string;
}

export function TrainingCenter() {
  const [isCreating, setIsCreating] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedChannelType, setSelectedChannelType] = useState<SimulatorChannel | null>(null);
  const navigate = useNavigate();
  const {
    simulators,
    loadingSimulators,
    handleCreateSimulator: createSimulator,
  } = useReleases();
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Track training center view
  useEffect(() => {
    posthogTrackEvent({
      event: OpportunitiesEvents.OPPORTUNITIES_VIEWED,
      properties: {
        page: "training_center",
        simulatorCount: simulators?.length || 0,
        timestamp: new Date().toISOString(),
      },
    });
  }, [simulators?.length]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleOpenCreateDialog = (type: SimulatorChannel) => {
    setSelectedChannelType(type);
    setIsCreateDialogOpen(true);
  };

  const handleDialogSubmit = async (name: string, resourceAccess: Record<string, ReleaseResourceAccessStatus>) => {
    if (!selectedChannelType) return;
    try {
      setIsCreating(true);
      const instance = await createSimulator(selectedChannelType, name, resourceAccess);
      setIsCreateDialogOpen(false);
      setTimeout(() => {
        navigate(`/automation/${instance.id}`);
      }, 300);
    } catch (error) {
      console.error("Error creating simulator:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const getLatestReleaseTime = (inst: { releases: SimulatorReleaseLite[] }) => {
    return (inst.releases || []).reduce(
      (latestTime, r: SimulatorReleaseLite) => {
        const currentTime = new Date(r?.createdAt ?? 0).getTime();
        return currentTime > latestTime ? currentTime : latestTime;
      },
      0
    );
  };

  const autopilotInstances = useMemo(() => {
    const instances =
      simulators?.filter((r) => !r.channel.includes("copilot")) || [];
    return instances.sort(
      (a, b) => getLatestReleaseTime(b) - getLatestReleaseTime(a)
    );
  }, [simulators]);

  const copilotInstances = useMemo(() => {
    const instances =
      simulators?.filter((r) => r.channel.includes("copilot")) || [];

    return instances.sort(
      (a, b) => getLatestReleaseTime(b) - getLatestReleaseTime(a)
    );
  }, [simulators]);

  const allInstances = useMemo(() => {
    return [...autopilotInstances, ...copilotInstances];
  }, [autopilotInstances, copilotInstances]);

  const renderTopicBadges = (topics: string[]) => {
    if (!topics?.length) return <Text color="gray">No live topics</Text>;

    const maxVisibleBadges = 3;
    const visibleTopics = topics.slice(0, maxVisibleBadges);
    const remainingCount = topics.length - maxVisibleBadges;

    return (
      <div className={styles.topicBadges}>
        {visibleTopics.map((topic, index) => (
          <Badge key={index} className={styles.topicBadge} color="gray">
            {topic}
          </Badge>
        ))}
        {remainingCount > 0 && (
          <HoverCard.Root>
            <HoverCard.Trigger>
              <Badge className={styles.hiddenBadge} color="gray">
                +{remainingCount} more
              </Badge>
            </HoverCard.Trigger>
            <HoverCard.Content>
              <Flex direction="column" gap="1">
                {topics.slice(maxVisibleBadges).map((topic, index) => (
                  <Text key={index} size="2">
                    {topic}
                  </Text>
                ))}
              </Flex>
            </HoverCard.Content>
          </HoverCard.Root>
        )}
      </div>
    );
  };

  const getTypeInfo = (channel: string) => {
    switch (channel) {
      case "autonomous_ticket_event":
        return {
          label: "Tickets",
          className: styles.tickets,
        };
      case "autonomous_quackchat":
        return {
          label: "Live Chat",
          className: styles.liveChat,
        };
      case "helpcenter":
        return {
          label: "Help Center",
          className: styles.helpCenter,
        };
      case "copilot":
        return {
          label: "Copilot",
          className: styles.coPilot,
        };
      case "slackbot":
        return {
          label: "Slackbot",
          className: styles.slackbot,
        };
      case "knowledge_hub":
        return {
          label: "Knowledge Hub",
          className: styles.knowledgeHub,
        };
      default:
        return {
          label: "Auto-pilot",
          className: styles.tickets,
        };
    }
  };

  const renderTableContent = (instances: any[]) => (
    <Table.Root>
      <Table.Header>
        <Table.Row className=" text-gray-500 font-normal">
          <Table.ColumnHeaderCell
            className=" font-normal"
            onClick={() => handleSort("name")}
            style={{ cursor: "pointer" }}
          >
            Name
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell
            className=" font-normal"
            onClick={() => handleSort("type")}
            style={{ cursor: "pointer" }}
          >
            Type
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell
            className=" font-normal"
            onClick={() => handleSort("topics")}
            style={{ cursor: "pointer" }}
          >
            Live Topics
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell
            className=" font-normal"
            onClick={() => handleSort("lastPublished")}
            style={{ cursor: "pointer" }}
          >
            Last Published
          </Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className=" font-normal">Status</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell className=" font-normal" align="right"></Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {instances.map((instance) => {
          const liveRelease = instance.releases?.find(
            (r: any) => r.status === "published"
          );

          const typeInfo = getTypeInfo(instance.channel);

          return (
            <AiInstance
              key={instance.id}
              instance={instance}
              typeInfo={typeInfo}
              renderTopicBadges={renderTopicBadges}
              formatDate={formatDate}
            />
          );
        })}
        {instances.length === 0 && (
          <Table.Row>
            <Table.Cell colSpan={6}>
              <Text
                align="center"
                size="2"
                color="gray"
                className={styles.emptyState}
              >
                No AI Agents yet. Get started by clicking + New AI Agent to
                create your first one.
              </Text>
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table.Root>
  );
  if (loadingSimulators) {
    return (
      <PageLayoutV2>
        <Flex justify="center" align="center" style={{ minHeight: "400px" }}>
          <Spinner size="3" />
        </Flex>
      </PageLayoutV2>
    );
  }

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <PageHeader
          title="Training Center"
          description="Manage and train your AI agents"
          icon={<AiBrowser size={24} fill={PageColors[ENavigate.TRAINING_CENTER] || "gray"} />}
          actions={
            <DropdownMenu
              align="end"
              items={[
                {
                  label: "Tickets",
                  onClick: () =>
                    handleOpenCreateDialog("autonomous_ticket_event"),
                  icon: (
                    <SimulatorChannelIcon
                      channel="autonomous_ticket_event"
                      size="2"
                    />
                  ),
                },
                {
                  label: "Live Chat",
                  onClick: () => handleOpenCreateDialog("autonomous_quackchat"),
                  icon: (
                    <SimulatorChannelIcon
                      channel="autonomous_quackchat"
                      size="2"
                    />
                  ),
                },
                // {
                //   label: "Help Center",
                //   onClick: () => handleOpenCreateDialog("helpcenter"),
                //   icon: <SimulatorChannelIcon channel="helpcenter" size="2" />,
                // },
                {
                  label: "Copilot",
                  onClick: () => handleOpenCreateDialog("copilot"),
                  icon: <SimulatorChannelIcon channel="copilot" size="2" />,
                },
                {
                  label: "Slackbot",
                  onClick: () => handleOpenCreateDialog("slackbot"),
                  icon: <SimulatorChannelIcon channel="slackbot" size="2" />,
                },
                {
                  label: "Knowledge Hub",
                  onClick: () => handleOpenCreateDialog("knowledge_hub"),
                  icon: (
                    <SimulatorChannelIcon channel="knowledge_hub" size="2" />
                  ),
                },
              ]}
            >
              <DropdownMenu.Trigger>
                <Button
                  variant="solid"
                  color="gray"
                  highContrast
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <Flex gap="2" align="center">
                      <Spinner size="1" />
                      Creating...
                    </Flex>
                  ) : (
                    <>
                      <PlusIcon />
                      New AI Agent
                    </>
                  )}
                </Button>
              </DropdownMenu.Trigger>
            </DropdownMenu>
          }
        />

        <Section size="4" pt="0">
          <Flex p="6" direction="column" gap="3" width="100%">
            <Flex direction={"row"} gap={"2"} align="center" justify="between">
              <Flex gap={"2"} align={"center"}>
                <Text size={"3"} weight={"medium"}>
                  AI Agents
                </Text>
                <Badge color="gray">{allInstances.length}</Badge>
              </Flex>
            </Flex>
            {renderTableContent(allInstances)}
          </Flex>
        </Section>
      </Flex>

      <CreateAIAgentDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        channelType={selectedChannelType}
        onSubmit={handleDialogSubmit}
        isCreating={isCreating}
      />
    </PageLayoutV2>
  );
}

function AiInstance({
  instance,
  typeInfo,
  renderTopicBadges,
  formatDate,
}: AiInstanceProps) {
  const [isEdit, setIsEdit] = useState(false);
  const [instanceName, setInstanceName] = useState(instance.name);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { handleUpdateSimulator, setChosenSimulatorId } = useReleases();
  const inputRef = useRef<HTMLInputElement>(null);
  const { topics: liveTopics, version: liveVersion } = useLiveEnabledTopics(
    instance.releases as SimulatorReleaseLite[]
  );

  const navigate = useNavigate();
  const liveRelease = (instance.releases as SimulatorReleaseLite[])?.find(
    (r: any) => r.status === "published"
  );

  const editInstanceName = async (name: string) => {
    await handleUpdateSimulator(instance.id, name);
    setIsEdit(false);
  };

  const { data: releasesWithConversations } = useGetSimulatorReleases(
    instance.id.toString(),
    false,
    false
  );

  const hasUpdates = useMemo(() => {
    if (!releasesWithConversations || !instance.releases) return false;

    const liveRelease = instance.releases.find(
      (r: any) => r.status === "published"
    );

    const currentDraft = instance.releases
      .filter((r: any) => r.status !== "published")
      .sort(
        (a: any, b: any) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];

    if (!currentDraft) return false;

    const getTopicsFromPolicy = (policy: any) => {
      try {
        const parsedPolicy =
          typeof policy === "string" ? JSON.parse(policy) : policy;
        if (!parsedPolicy) return {};

        const topicInstructions = parsedPolicy.reduce(
          (acc: Record<string, any[]>, instruction: any) => {
            if (instruction.topic) {
              if (!acc[instruction.topic]) {
                acc[instruction.topic] = [];
              }
              acc[instruction.topic].push(instruction);
            }
            return acc;
          },
          {}
        );

        return topicInstructions;
      } catch (error) {
        console.error("Error parsing release policy:", error);
        return {};
      }
    };

    const hasAnyFeedbackConversations = releasesWithConversations
      ?.filter((release: any) => release.id === currentDraft.id)
      .some((release: any) =>
        release.conversations?.some(
          (conv: any) =>
            conv.feedback === "POSITIVE" || conv.feedback === "NEGATIVE"
        )
      );

    if (hasAnyFeedbackConversations) {
      return true;
    }

    // Check for topic-specific updates
    const hasTopicUpdates = (topic: string): boolean => {
      const draftTopics = getTopicsFromPolicy(currentDraft.releasePolicy);
      const liveTopics = liveRelease
        ? getTopicsFromPolicy(liveRelease.releasePolicy)
        : {};

      const currentInstructions = draftTopics[topic] || [];
      const liveInstructions = liveTopics[topic] || [];

      const currentManualInstructions = currentInstructions.filter(
        (i: any) =>
          i.type === "manual" &&
          i.content !== "Think carefully before answering"
      );
      const liveManualInstructions = liveInstructions.filter(
        (i: any) =>
          i.type === "manual" &&
          i.content !== "Think carefully before answering"
      );

      const currentBriefs = currentInstructions.filter(
        (i: any) =>
          i.type === "brief" &&
          i.subTopic === null &&
          i.content &&
          i.content.trim()
      );
      const liveBriefs = liveInstructions.filter(
        (i: any) =>
          i.type === "brief" &&
          i.subTopic === null &&
          i.content &&
          i.content.trim()
      );

      const currentToggles = {
        knowledge:
          currentInstructions.find(
            (i: any) => i.type === "brief" && i.subTopic === "knowledge"
          )?.content === "true",
        tickets:
          currentInstructions.find(
            (i: any) => i.type === "brief" && i.subTopic === "tickets"
          )?.content === "true",
      };

      const liveToggles = {
        knowledge:
          liveInstructions.find(
            (i: any) => i.type === "brief" && i.subTopic === "knowledge"
          )?.content === "true",
        tickets:
          liveInstructions.find(
            (i: any) => i.type === "brief" && i.subTopic === "tickets"
          )?.content === "true",
      };

      const hasToggleChanges =
        currentToggles.knowledge !== liveToggles.knowledge ||
        currentToggles.tickets !== liveToggles.tickets;

      const instructionsChanged =
        JSON.stringify(currentManualInstructions) !==
        JSON.stringify(liveManualInstructions);
      const briefsChanged =
        JSON.stringify(currentBriefs) !== JSON.stringify(liveBriefs);

      const hasActualChanges =
        instructionsChanged || briefsChanged || hasToggleChanges;

      return hasActualChanges;
    };

    const draftTopics = getTopicsFromPolicy(currentDraft.releasePolicy);
    const allTopics = Object.keys(draftTopics);

    return allTopics.some((topic: string) => hasTopicUpdates(topic));
  }, [releasesWithConversations, instance.releases]);

  const actions = useMemo<DropdownMenuItem[]>(
    () => [
      {
        label: "Continue Training",
        onClick: () => navigate(`/automation/${instance.id}`),
        icon: <PlayIcon />,
      },
      {
        label: "Version History",
        onClick: () => navigate(`/automation/${instance.id}/versions`),
        icon: <ClockIcon />,
      },
      {
        label: "Rename",
        onClick: () => {
          setIsEdit(true);
          setChosenSimulatorId(instance.id);
        },
        icon: <Pencil1Icon />,
      },
      {
        label: "Delete",
        onClick: () => setIsDeleteOpen(true),
        icon: <TrashIcon />,
      },
    ],
    [instance.id]
  );

  return (
    <>
      <Table.Row key={instance.id} className={styles.tableRow}>
        <Table.Cell
          onClick={() => navigate(`/automation/${instance.id}`)}
        >
          <Flex align="center" gap="2">
            <SimulatorChannelIcon channel={instance.channel} size="2" />
            {isEdit ? (
              <Input
                ref={inputRef}
                className={" text-xs h-6 w-3/4"}
                value={instanceName}
                onChange={(e) => setInstanceName(e.target.value)}
                autoFocus
                onClick={(e) => e.stopPropagation()}
                onBlur={(e) => {
                  e.stopPropagation();
                  editInstanceName(instanceName);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    editInstanceName(instanceName);
                  }
                }}
              />
            ) : (
              <Text weight="medium">{instance.name}</Text>
            )}
          </Flex>
        </Table.Cell>
        <Table.Cell onClick={() => navigate(`/automation/${instance.id}`)}>
          <div className={`${styles.typeLabel} ${typeInfo.className}`}>
            {typeInfo.label}
          </div>
        </Table.Cell>
        <Table.Cell onClick={() => navigate(`/automation/${instance.id}`)}>
          {renderTopicBadges(liveTopics)}
        </Table.Cell>
        <Table.Cell onClick={() => navigate(`/automation/${instance.id}`)}>
          <Flex align="center" gap="2">
            {liveRelease ? (
              <>
                {formatDate(
                  liveRelease.publishedAt
                    ? typeof liveRelease.publishedAt === "string"
                      ? liveRelease.publishedAt
                      : liveRelease.publishedAt.toISOString()
                    : ""
                )}
                <Badge size="1" color="green">
                  v{liveVersion}
                </Badge>
              </>
            ) : (
              <Text size="2" color="gray">
                No live version
              </Text>
            )}
          </Flex>
        </Table.Cell>
        <Table.Cell onClick={() => navigate(`/automation/${instance.id}`)}>
          <Flex gap="2">
            {liveRelease && (
              <Badge size="1" color="green">
                Live
              </Badge>
            )}
            {hasUpdates && (
              <Badge size="1" color="blue">
                Updates Available
              </Badge>
            )}
            {!liveRelease && !hasUpdates && (
              <Text size="2" color="gray">
                Training not started yet
              </Text>
            )}
          </Flex>
        </Table.Cell>
        <Table.Cell>
          <Flex gap="2" justify="end" align="center">
            <DropdownMenu items={actions} align="end">
              <DropdownMenu.Trigger>
                <Button size="1" variant="ghost" color="gray">
                  <DotsVerticalIcon />
                </Button>
              </DropdownMenu.Trigger>
            </DropdownMenu>
          </Flex>
        </Table.Cell>
      </Table.Row>
      {isDeleteOpen && (
        <DeleteSimulatorDialog
          open={isDeleteOpen}
          simulatorId={instance.id}
          onClose={() => {
            setIsDeleteOpen(false);
          }}
          simulatorName={instance.name}
        />
      )}
    </>
  );
}
